import type { AudioEngine } from '../audio/audio';
import type { TouchControls } from '../input/touch';
import type { StatsReporter } from '../net/statsReporter';
import type { LessonOverlay } from '../render/lessonOverlay';
import type { CarState } from '../sim/car';
import { type InputFrame, quantizeInput } from '../sim/input';
import type { TrackRuntime } from '../sim/trackQuery';
import type { TrackData } from '../track/types';
import { botInput, type BotPlan, trackPosition } from '../track/bot';
import type { Controls, LessonUi } from '../ui/lessonUi';
import type { Session } from './session';
import { barFill, barZone, type CoachEvent, LessonProgress, type LessonTurn, type Observation, observe, profileAt, TurnCoach } from './lesson';
import type { LapResult } from './session';

/**
 * The driving lesson's running order, on top of the coach (lesson.ts), the
 * cards (ui/lessonUi.ts) and the road markings (render/lessonOverlay.ts):
 * the offer, the controls, coached tries at turn 1 until the player gets
 * round it on their own (with an instructor demo and a hidden brake assist
 * for those who keep struggling), lighter cues for the rest of the lap, and
 * the end card. The lesson lap is practice (Session.practice): nothing is
 * saved or put on the board. The player can leave at any moment.
 *
 * The game (main.ts) keeps stepping the sim; the director says when it's
 * frozen, what input goes in (the instructor drives the demo; the assist
 * brakes), and reacts after every step.
 */
export type LessonEntry = 'first' | 'poster' | 'nudge';

type Stage = 'offer' | 'intro' | 'attempt' | 'fail' | 'passOwn' | 'demo' | 'ready' | 'rest' | 'hold' | 'end';

/** What the director needs from the game. */
export interface LessonHost {
  session(): Session;
  runtime(): TrackRuntime;
  track(): TrackData;
  /** The beginner plan (gentler than the bot's), and its braking events round the lap. */
  plan(): BotPlan;
  /** The green zone's top: a quicker plan, the most that's still fine (only over it is a driver too fast). */
  top(): BotPlan;
  events(): LessonTurn[];
  /** Turn 1: the first real stop after the start, and its index in events(). */
  turn(): LessonTurn;
  turnIndex(): number;
  spawnIndex(): number;
  controls(): Controls;
  ui: LessonUi;
  touch: TouchControls;
  audio: AudioEngine;
  stats: StatsReporter;
  overlay(): LessonOverlay | null;
  /** Put the car back at the start (Game.restart). */
  restart(): void;
  /** The lesson is over: race for real, or show how to play. */
  finish(next: 'race' | 'how'): void;
  /** Keep a word on this browser (the lesson taken, skipped or done). */
  remember(value: 'taken' | 'skipped' | 'done'): void;
}

const kmh = (ms: number): number => Math.round(ms * 3.6);

/** A card that answers to the gas only after this long (so a reflex press doesn't wave away what went wrong). */
const GAS_CARD_MS = 900;
/** Esc, B or Start this soon after the offer appears is the same press that opened it, not an answer. */
const OFFER_ARM_MS = 500;

export class LessonDirector {
  private stage: Stage;
  /** The freeze that holds the sim while the player brakes ('brake now', 'keep braking'), if any. */
  private freeze: 'brake' | 'keep' | null = null;
  private coach: TurnCoach | null = null;
  private readonly progress = new LessonProgress();
  private failNext: 'retry' | 'demo' = 'retry';
  private assisted = false;
  /** The event the rest-of-lap cues are on (an index into host.events()). */
  private restAt = -1;
  /** Where to go back to after a hold (the tab was put away). */
  private held: Stage | null = null;
  /** A card continues on the gas only once the gas has been let go since the card appeared. */
  private gasArmed = false;
  /** When the current card appeared (for the gas and offer guards). */
  private cardAt = 0;
  private last: Observation | null = null;
  /** The controls the lesson's words were last drawn for (a controller picked up mid-lesson redraws them). */
  private drawnFor: Controls;

  constructor(
    private host: LessonHost,
    offer: boolean,
    private readonly challenger: string | null,
  ) {
    host.session().practice = true;
    this.drawnFor = host.controls();
    this.cardAt = performance.now();
    if (offer) {
      this.stage = 'offer';
      host.stats.count('lessonOffered');
      host.stats.reach('offer');
      host.ui.showOffer({ controls: host.controls(), challenger });
      host.audio.setCarSounds(false);
    } else {
      this.stage = 'intro';
      host.stats.count('lessonTaken');
      host.stats.lessonChoice(true);
      host.remember('taken');
      this.intro();
    }
  }

  /** The sim is held still (a card is up, or the player must brake to go on). */
  get frozen(): boolean {
    return (
      this.freeze !== null ||
      this.stage === 'offer' ||
      this.stage === 'intro' ||
      this.stage === 'fail' ||
      this.stage === 'passOwn' ||
      this.stage === 'ready' ||
      this.stage === 'hold' ||
      this.stage === 'end'
    );
  }

  /** The instructor's demo runs at half speed. */
  get speedScale(): number {
    return this.stage === 'demo' ? 0.5 : 1;
  }

  /** Nobody's driving: the demo, or a card. Not counted as time racing. */
  get driving(): boolean {
    return !this.frozen && this.stage !== 'demo';
  }

  // --- the player's choices (cards, keys, buttons) ---

  take(): void {
    if (this.stage !== 'offer') return;
    this.host.stats.count('lessonTaken');
    this.host.stats.reach('answered');
    this.host.stats.lessonChoice(true);
    this.host.remember('taken');
    this.intro();
  }

  skip(): void {
    if (this.stage !== 'offer') return;
    this.host.stats.count('lessonSkipped');
    this.host.stats.reach('answered');
    this.host.stats.lessonChoice(false);
    this.host.remember('skipped');
    this.close('race');
  }

  /** Leave now, from anywhere: straight into a real race. */
  leave(): void {
    if (this.stage === 'offer') return this.skip();
    if (this.stage !== 'end') this.host.stats.count('lessonLeft');
    this.close('race');
  }

  /** The card's main button (Enter, Space, controller A, a tap). */
  proceed(): void {
    switch (this.stage) {
      case 'offer':
        return this.take();
      case 'intro':
        return this.attempt();
      case 'fail':
        return this.failNext === 'demo' ? this.demo() : this.attempt();
      case 'passOwn':
      case 'ready':
        return this.attempt();
      case 'hold':
        return this.resumeHold();
      case 'end':
        return this.close('race');
    }
  }

  raceForReal(): void {
    this.close('race');
  }

  howToPlay(): void {
    this.close('how');
  }

  skipDemo(): void {
    if (this.stage === 'demo') this.endDemo();
  }

  /** The game's actions while the lesson runs. Returns true when handled. */
  onAction(a: 'restart' | 'pause' | 'back' | 'confirm' | 'start' | string): boolean {
    if (a === 'pause' || a === 'back') {
      // The press that opened the offer (a quick double Start) isn't an answer to it.
      if (this.stage === 'offer' && performance.now() - this.cardAt < OFFER_ARM_MS) return true;
      if (this.stage === 'end') this.close('race');
      else this.leave();
      return true;
    }
    if (a === 'confirm') {
      if (this.host.ui.cardUp) this.host.ui.confirm();
      else if (this.stage === 'hold') this.resumeHold();
      return true;
    }
    if (a === 'restart') {
      // R, Y and ↺ mean "retry": a try of your own from the start (not counted as a miss), the card's
      // "try again" on a fail or pass card, and on the end card, how to play.
      if (this.stage === 'attempt' && !this.freeze) this.attempt(false);
      else if (this.stage === 'fail' || this.stage === 'passOwn' || this.stage === 'ready' || this.stage === 'hold') this.host.ui.confirm();
      else if (this.stage === 'rest') this.restFromStart();
      else if (this.stage === 'demo') this.endDemo();
      else if (this.stage === 'end') this.close('how');
      return true;
    }
    return a === 'start';
  }

  /** The tab was put away mid-lesson: hold everything until they're back and ready. */
  hold(): void {
    if (this.frozen) return;
    this.held = this.stage;
    this.stage = 'hold';
    this.host.audio.setCarSounds(false);
    this.host.ui.showReady({ controls: this.host.controls(), kind: 'held' });
    this.gasArmed = false;
    this.cardAt = performance.now();
  }

  // --- the sim loop ---

  /** The input that goes into this sim step: the instructor drives the demo; the assist brakes for strugglers. */
  frame(f: InputFrame): InputFrame {
    const s = this.host.session();
    const car = s.state.car;
    if (this.stage === 'demo') return botInput(car, this.host.runtime(), this.host.plan(), s.physics);
    if (this.stage === 'attempt' && this.progress.assist && this.inBraking(car)) {
      const bot = botInput(car, this.host.runtime(), this.host.plan(), s.physics);
      if (bot.brake > f.brake) return quantizeInput(0, bot.brake / 255, f.steer / 127);
    }
    return f;
  }

  /** After each sim step. Returns true when the sim must stop stepping this frame (a freeze or a card). */
  afterStep(f: InputFrame): boolean {
    const coach = this.coach;
    // (The lap that just ended may have put the end card up inside this very step.)
    if (!coach) return this.frozen;
    this.last = this.observe(this.host.session().state.car, f);
    const ev = coach.step(this.last);
    if (ev) this.onEvent(ev);
    return this.frozen;
  }

  /** Every frame while frozen: the brake lifts a freeze; the gas (freshly pressed) moves a card on. */
  whileFrozen(throttle: number, brake: number): void {
    if (this.freeze) {
      if (this.coach?.canResume(brake)) {
        this.freeze = null;
        this.host.ui.setSqueeze(false);
        this.host.ui.hideCard();
        this.host.audio.setCarSounds(true);
      } else this.host.ui.setSqueeze(brake > 0.05);
      return;
    }
    if (throttle < 0.2) {
      this.gasArmed = true;
      return;
    }
    if (throttle < 0.5 || !this.gasArmed) return;
    // The cards that say "press the gas" go on at once; a fail or pass card only once it's had time to be read.
    const says = this.stage === 'intro' || this.stage === 'ready' || this.stage === 'hold';
    const read = (this.stage === 'fail' || this.stage === 'passOwn') && performance.now() - this.cardAt >= GAS_CARD_MS;
    if (says || read) this.proceed();
  }

  /** Every frame: the caption, the speed bar, the markings on the road, the pedal cue. */
  display(car: CarState, t: number): void {
    const ui = this.host.ui;
    const overlay = this.host.overlay();
    const coach = this.coach;
    const o = this.last ?? this.observe(car, { throttle: 0, brake: 0, steer: 0 });
    // A controller picked up (or put down) mid-lesson: the ✕ and the cards name its buttons from now on.
    const controls = this.host.controls();
    if (controls !== this.drawnFor) {
      this.drawnFor = controls;
      ui.setControls(controls);
    }
    overlay?.update(trackPosition(car, this.host.runtime()), t);
    const cardStage = this.stage === 'offer' || this.stage === 'intro' || this.stage === 'end' || this.stage === 'hold' || this.stage === 'ready';
    if (!coach || cardStage) {
      ui.setCue(null);
      ui.setBar(null);
      this.host.touch.cue(this.stage === 'intro' || this.stage === 'hold' || this.stage === 'ready' ? 'gas' : null);
      return;
    }
    const d = coach.display(o);
    ui.setCue({ phase: d.phase, flash: d.flash, dir: this.dirAhead(o.pos), controls, demo: this.stage === 'demo' });
    if (d.bar) ui.setBar({ speedKmh: kmh(d.bar.speed), targetKmh: kmh(d.bar.target), topKmh: kmh(d.bar.top), fill: barFill(d.bar), zone: barZone(d.bar), ok: d.bar.ok, final: d.bar.final });
    else ui.setBar(null);
    const brakeCue = this.freeze !== null || d.flash || d.phase === 'keep' || (d.phase === 'brake' && o.brake < 0.4);
    this.host.touch.cue(this.stage !== 'demo' && brakeCue ? 'brake' : null);
  }

  /** The practice lap is over (only its end matters: nothing is saved). */
  onLap(r: LapResult): void {
    if (this.stage !== 'rest') return;
    this.stage = 'end';
    this.coach = null;
    this.host.stats.count('lessonDone');
    this.host.remember('done');
    this.host.audio.setCarSounds(false);
    this.host.overlay()?.showEvents('none');
    this.host.ui.showEnd({ tries: this.progress.fails + 1, lapMs: r.ms, valid: r.valid, challenger: this.challenger, assisted: this.assisted });
  }

  // --- the running order ---

  private intro(): void {
    this.stage = 'intro';
    this.host.ui.setActive(true, this.host.controls());
    this.host.ui.showIntro({ controls: this.host.controls(), dir: this.host.turn().dir });
    this.host.audio.setCarSounds(false);
    this.markings('turn');
    this.gasArmed = false;
    this.cardAt = performance.now();
  }

  /** A try at turn 1, from the start. `counted`: false for a retry of your own (R, Y, ↺ mid-try). */
  private attempt(counted = true): void {
    this.host.restart();
    if (counted || this.progress.tries === 0) this.progress.startAttempt();
    this.coach = new TurnCoach(this.host.turn(), this.host.plan().profile, this.host.spawnIndex(), this.host.runtime().n, this.progress.level, this.host.top().profile);
    this.stage = 'attempt';
    this.freeze = null;
    this.last = null;
    this.host.ui.hideCard();
    this.host.audio.setCarSounds(true);
    this.markings('turn');
  }

  /** The rest of the practice lap, after turn 1: the player is on their own (main.ts's stuck and crash hints). */
  get practising(): boolean {
    return this.stage === 'rest' && !this.frozen;
  }

  private onEvent(ev: CoachEvent): void {
    if (ev.type === 'freeze') {
      this.freeze = ev.why;
      this.host.audio.setCarSounds(false);
      this.host.ui.showFreeze({ why: ev.why, controls: this.host.controls(), speedKmh: kmh(ev.speed), targetKmh: kmh(ev.target), final: ev.final });
      return;
    }
    if (this.stage === 'demo') {
      if (ev.type === 'pass' || ev.type === 'fail') this.endDemo();
      return;
    }
    if (this.stage === 'rest') {
      if (ev.type === 'pass') this.nextRest();
      return;
    }
    if (this.stage !== 'attempt') return;
    if (ev.type === 'fail') {
      this.failNext = this.progress.onFail();
      if (this.progress.assist && !this.assisted) {
        this.assisted = true;
        this.host.stats.count('lessonAssist');
      }
      this.stage = 'fail';
      this.coach = null;
      this.host.audio.setCarSounds(false);
      this.host.ui.showFail({
        why: ev.why,
        controls: this.host.controls(),
        arrivedKmh: ev.arrived === null ? null : kmh(ev.arrived),
        targetKmh: kmh(ev.target),
        braked: ev.braked,
        tries: this.progress.tries,
        next: this.failNext,
        early: ev.early,
        speedKmh: kmh(ev.speed),
        tooFast: ev.tooFast,
        assisted: this.progress.assist,
      });
      this.gasArmed = false;
      this.cardAt = performance.now();
      return;
    }
    // Round turn 1.
    const next = this.progress.onPass();
    if (next === 'own') {
      this.stage = 'passOwn';
      this.coach = null;
      this.host.audio.setCarSounds(false);
      this.host.ui.showPass({ next: 'own', tries: this.progress.tries, assisted: this.assisted });
      this.gasArmed = false;
      this.cardAt = performance.now();
      return;
    }
    this.host.stats.count('lessonTurnOne');
    // Tries as a player counts them: the misses, plus the one that made it (a clean run is a first try).
    this.host.stats.count('lessonTries', this.progress.fails + 1);
    this.host.ui.showPass({ next: 'mastered', tries: this.progress.fails + 1, assisted: this.assisted });
    // Carry on round the lap with lighter cues.
    this.stage = 'rest';
    this.markings('all');
    this.restAt = this.host.turnIndex();
    this.nextRest();
  }

  /** The rest of the lap: the cues move on to the next braking zone. */
  private nextRest(): void {
    const events = this.host.events();
    if (!events.length) {
      this.coach = null;
      return;
    }
    this.restAt = (this.restAt + 1) % events.length;
    this.coach = new TurnCoach(events[this.restAt], this.host.plan().profile, this.nearestIndex(), this.host.runtime().n, 'free', this.host.top().profile);
  }

  /** A restart during the rest of the lap: the cues start again from turn 1. */
  private restFromStart(): void {
    this.host.restart();
    this.restAt = this.host.turnIndex() - 1;
    this.last = null;
    this.nextRest();
  }

  private demo(): void {
    this.host.stats.count('lessonDemo');
    this.host.restart();
    this.stage = 'demo';
    this.coach = new TurnCoach(this.host.turn(), this.host.plan().profile, this.host.spawnIndex(), this.host.runtime().n, 'free', this.host.top().profile);
    this.last = null;
    this.host.ui.hideCard();
    this.host.ui.showDemo(true, this.host.controls());
    this.host.audio.setCarSounds(true);
    this.markings('turn');
  }

  /** The demo is over: a moment to take the wheel ("your go") before the player's own try. */
  private endDemo(): void {
    this.progress.demoDone();
    this.host.ui.showDemo(false);
    this.host.restart();
    this.coach = null;
    this.stage = 'ready';
    this.host.audio.setCarSounds(false);
    this.host.ui.showReady({ controls: this.host.controls(), kind: 'afterDemo' });
    this.gasArmed = false;
    this.cardAt = performance.now();
  }

  private resumeHold(): void {
    if (this.stage !== 'hold' || !this.held) return;
    this.stage = this.held;
    this.held = null;
    this.host.ui.hideCard();
    this.host.audio.setCarSounds(true);
  }

  /** Finish the lesson: tidy everything away and hand over to the game. */
  private close(next: 'race' | 'how'): void {
    const ui = this.host.ui;
    this.coach = null;
    this.freeze = null;
    ui.hideCard();
    ui.showDemo(false);
    ui.setCue(null);
    ui.setBar(null);
    ui.setActive(false, this.host.controls());
    this.host.touch.cue(null);
    this.host.session().practice = false;
    this.host.finish(next);
  }

  // --- helpers ---

  /** Which braking ribbons the road shows: turn 1's alone while it's being learnt, or all of them. */
  private markings(which: 'turn' | 'all'): void {
    const overlay = this.host.overlay();
    if (!overlay) return;
    overlay.setLine(true);
    const i = this.host.turnIndex();
    overlay.showEvents(which === 'all' || i < 0 ? 'all' : i);
  }

  /**
   * Which way the road bends next (for the caption's arrow): the corner the car is in, or the next one ahead.
   * A stop can span two corners (a sweeper into a hairpin), so this follows the road rather than the stop.
   */
  private dirAhead(pos: number): 1 | -1 {
    const t = this.host.track();
    const n = t.n;
    let best: { dir: number; ahead: number } | null = null;
    for (const c of t.corners) {
      if (Math.abs(c.angle) < 0.25) continue;
      const into = (((pos - c.start) % n) + n) % n;
      const len = (((c.end - c.start) % n) + n) % n;
      if (into <= len) return c.dir > 0 ? 1 : -1;
      const ahead = (((c.start - pos) % n) + n) % n;
      if (!best || ahead < best.ahead) best = { dir: c.dir, ahead };
    }
    return best ? (best.dir > 0 ? 1 : -1) : this.host.turn().dir;
  }

  /** The car's sample index now (a free coach counts its progress from here). */
  private nearestIndex(): number {
    return Math.floor(trackPosition(this.host.session().state.car, this.host.runtime()));
  }

  /** Inside turn 1's braking, faster than the beginner plan wants (where the assist may brake). */
  private inBraking(car: CarState): boolean {
    const rt = this.host.runtime();
    const n = rt.n;
    const t = this.host.turn();
    const spawn = this.host.spawnIndex();
    const prog = (i: number): number => (((i - spawn) % n) + n) % n;
    const pos = trackPosition(car, rt);
    const p = prog(pos);
    if (p < prog(t.brakeIndex) - 10 || p > prog(t.minIndex)) return false;
    return Math.hypot(car.vx, car.vy) > profileAt(this.host.plan().profile, pos) + 0.5;
  }

  /** What the coach sees this tick (the frame actually sent to the sim, after any assist). */
  private observe(car: CarState, f: InputFrame): Observation {
    return observe(car, this.host.runtime(), f);
  }
}
